import * as Components from '../components'
import * as Form from '../form'
import * as Hooks from '../hooks'
import * as Navigation from '../navigation'
import * as Stores from '../stores'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SizableText, View } from 'tamagui'
import { UnitedStatesIcon } from '@/src/assets/icons/flags/UnitedStatesIcon'
import { Mail } from '@/src/assets/icons/Mail'
import { mesaColorNames } from '@/src/common/theme'
import { router } from 'expo-router'
import { z } from 'zod/v4'

const ReviewFormSchema = Form.CardApplicationFormSchema.omit({ acceptCardholderAgreement: true })
type ReviewFormValues = z.infer<typeof ReviewFormSchema>

export function ReviewScreen() {
  const updateField = Stores.useCardApplicationFormStore((s) => s.updateField)
  const cardApplication = Stores.useCardApplicationFormStore((s) => s.cardApplicationForm)

  const form = useForm<ReviewFormValues>({
    defaultValues: cardApplication,
    mode: 'onBlur',
    resolver: zodResolver(ReviewFormSchema),
  })
  const { registerNav } = Hooks.useFormNav(form, [
    'firstName',
    'lastName',
    'phoneNumber',
    'email',
    'dob',
    'ssn',
    'totalAnnualIncome',
    'monthlyHousingPayment',
  ])
  const onSubmit = form.handleSubmit((data) => {
    updateField('firstName', data.firstName)
    updateField('lastName', data.lastName)
    updateField('phoneNumber', data.phoneNumber)
    updateField('email', data.email)
    updateField('dob', data.dob)
    updateField('ssn', data.ssn)
    updateField('totalAnnualIncome', data.totalAnnualIncome)
    updateField('monthlyHousingPayment', data.monthlyHousingPayment)
    router.push(Navigation.Routes.ApplicationCardHolderAgreement.route)
  })
  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Header previousRoute={Navigation.Routes.LastQuestion} />
        <Components.ApplicationScreen.Content title="Make sure everything looks right">
          <Components.FormHelpText>
            Review your information below and make any edits as needed prior to submission
          </Components.FormHelpText>
          <Components.FormInput
            control={form.control}
            name="firstName"
            label="First name"
            {...Form.FormInputProps.firstName}
            {...registerNav('firstName')}
          />
          <Components.FormInput
            control={form.control}
            name="lastName"
            label="Last name"
            {...Form.FormInputProps.lastName}
            {...registerNav('lastName')}
          />
          <Components.FormInput
            control={form.control}
            name="phoneNumber"
            label="Phone number"
            {...Form.FormInputProps.phoneNumber}
            leftIcon={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <UnitedStatesIcon />
                <SizableText>+1</SizableText>
              </View>
            }
          />
          <Components.FormInput
            control={form.control}
            name="email"
            label="Email address"
            {...Form.FormInputProps.email}
            leftIcon={<Mail stroke={mesaColorNames['Greyscale/400 #A4AAAC']} />}
          />
          <Components.FormInputDateOfBirth
            control={form.control}
            name="dob"
            label="Date of birth"
            {...Form.FormInputProps.dob}
            {...registerNav('dob')}
          />
          <Components.FormInputSsn
            control={form.control}
            name="ssn"
            label="Social security number"
            {...Form.FormInputProps.ssn}
            {...registerNav('ssn')}
          />
          <Components.FormInputCurrency
            control={form.control}
            name="totalAnnualIncome"
            label="Total annual income"
            {...Form.FormInputProps.totalAnnualIncome}
            {...registerNav('totalAnnualIncome')}
          />
          <Components.FormInputCurrency
            control={form.control}
            name="monthlyHousingPayment"
            label="Monthly housing payment"
            {...Form.FormInputProps.monthlyHousingPayment}
            {...registerNav('monthlyHousingPayment')}
          />
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void onSubmit()}
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
          title="Continue"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
